/**
 * Pure UA-based platform detection for the Add-to-Home-Screen guide.
 *
 * Kept free of `window` access so it is unit-testable under Node: callers pass
 * the UA string plus the already-resolved standalone flag (from
 * `matchMedia('(display-mode: standalone)')` / `navigator.standalone`) and
 * `navigator.maxTouchPoints` (needed because iPadOS ships a desktop Mac UA).
 */

export type InstallPlatform =
  /** Already running as an installed app — show nothing. */
  | 'standalone'
  /** Real Safari on iPhone/iPad — can Add to Home Screen directly. */
  | 'ios-safari'
  /**
   * Any other iOS browser or in-app webview (Gmail/GSA, Facebook, Instagram,
   * Chrome/Firefox/Edge on iOS, …) — must hand off to Safari first.
   */
  | 'ios-other'
  /** Chrome on Android — `beforeinstallprompt` one-tap install path. */
  | 'android-chrome'
  /** Other Android browsers/webviews — menu-based fallback instructions. */
  | 'android-other'
  | 'desktop';

export interface InstallEnvironment {
  /** display-mode standalone or `navigator.standalone` already true. */
  standalone?: boolean;
  /** `navigator.maxTouchPoints`; distinguishes iPadOS from a real Mac. */
  maxTouchPoints?: number;
}

/**
 * iOS browsers and in-app webviews that are not Safari. Order is irrelevant;
 * any hit means "send them to Safari". GSA is the Google app (also Gmail's
 * in-app browser); FBAN/FBAV/FB_IAB are Facebook; the rest are third-party
 * browsers that iOS forces onto WebKit but that still cannot Add to Home
 * Screen themselves.
 */
const IOS_NON_SAFARI =
  /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|FBAN|FBAV|FB_IAB|Instagram|DuckDuckGo|YaBrowser|Line\/|MicroMessenger|Snapchat|LinkedInApp|musical_ly|BytedanceWebview|Twitter/i;

/** Android browsers that carry a `Chrome/` token but are not Chrome. */
const ANDROID_NON_CHROME =
  /EdgA|OPR\/|SamsungBrowser|YaBrowser|UCBrowser|MiuiBrowser|HuaweiBrowser|DuckDuckGo/i;

export function detectInstallPlatform(ua: string, env: InstallEnvironment = {}): InstallPlatform {
  if (env.standalone) return 'standalone';

  const isIpadDesktopUa = /Macintosh/.test(ua) && (env.maxTouchPoints ?? 0) > 1;
  const isIos = /iPad|iPhone|iPod/.test(ua) || isIpadDesktopUa;
  if (isIos) {
    if (IOS_NON_SAFARI.test(ua)) return 'ios-other';
    // Real Safari carries a `Safari/` token; bare-WebKit in-app views do not.
    return /Safari\//.test(ua) ? 'ios-safari' : 'ios-other';
  }

  if (/Android/.test(ua)) {
    const isWebview = /; wv\)/.test(ua);
    const isChrome =
      /Chrome\/\d/.test(ua) && !isWebview && !ANDROID_NON_CHROME.test(ua) && !/Firefox\//.test(ua);
    return isChrome ? 'android-chrome' : 'android-other';
  }

  return 'desktop';
}

export function isMobileInstallPlatform(
  platform: InstallPlatform,
): platform is 'ios-safari' | 'ios-other' | 'android-chrome' | 'android-other' {
  return platform !== 'standalone' && platform !== 'desktop';
}

export {
  isStandaloneDisplay,
  PWA_STANDALONE_START_PATH,
  shouldRedirectStandaloneFromMarketingHome,
} from '@/lib/pwa/app-launch';
